import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ANDROID_APP_LINK, IOS_APP_LINK, SUPPORT_EMAIL } from 'src/app/constants/emailAndLinks';
import { NotifyMePayload, NotifyMeService } from 'src/app/service/notify-me.service';
declare var AOS: any;
@Component({ selector: 'app-hw-dashcam', templateUrl: './dashcam.component.html', styleUrls: ['./dashcam.component.scss'], standalone: false })
export class DashCamComponent implements OnInit, AfterViewInit, OnDestroy {
  activeSection = 'overview';
  supportEmail = SUPPORT_EMAIL;
  androidAppLink = ANDROID_APP_LINK;
  iosAppLink = IOS_APP_LINK;
  isNotifyModalOpen = false;
  isSubmittingNotify = false;
  isNotifySubmitSuccess = false;
  notifyToastMessage = 'Thank you, we will notify you once the product is available.';
  notifyForm = {
    name: '',
    email: '',
    phone: ''
  };

  private notifySuccessTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private notifyQueryParamSubscription?: Subscription;

  @ViewChild('notifyDialog') private notifyDialog?: ElementRef<HTMLDialogElement>;

  sections = [
    { id: 'overview', label: 'Overview', icon: 'bx-layer' },
    { id: 'setup', label: 'Setup', icon: 'bx-mobile-alt' },
    { id: 'app', label: 'App Features', icon: 'bx-grid-alt' },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: 'bx-wrench' },
    { id: 'safety', label: 'Safety', icon: 'bx-shield-quarter' },
  ];
  specs = [
    { label: 'Video', value: '2K Front + 1K Rear Dual Cam' },
    { label: 'Lens', value: 'Wide Angle 130°' },
    { label: 'Recording', value: 'Loop Recording (1 / 2 / 3 mins)' },
    { label: 'Power', value: 'Type-C USB ACC Power' },
    // { label: 'Storage', value: 'Supports up to 256GB microSD' },
    { label: 'Detection', value: 'G-Sensor + Motion Detection' },
    { label: 'Connectivity', value: 'Wi-Fi for App Access' },
    { label: 'Warranty', value: '1-Year Warranty' },
  ];
  installSteps = [
    'Download the Varroc Connect App from Google Play Store or Apple App Store.',
    'Open the app and register or log in using your mobile number.',
    'Follow the on-screen steps to add a new device.',
  ];
  installActions = ['Download', 'Log in', 'Follow Steps'];
  wifiSteps = [
    'Turn on the dashcam.',
    'On your phone, search for a Wi-Fi network starting with CAMZO_wifi-Xsgbnx.',
    'Connect using the default password: 12345678.',
    'Open the app, select dashcam, enter the Dashcam ID, and reset the password.',
  ];
  appFeatures = [
    { icon: 'bx-video', title: 'Live View', desc: 'Watch real-time video directly inside the Varroc Connect App.' },
    { icon: 'bx-download', title: 'Playback & Download', desc: 'Review trip footage and save important videos when needed.' },
    { icon: 'bx-line-chart', title: 'Trip Reports', desc: 'Get driving behavior insights and trip-level visibility.' },
    { icon: 'bx-bell', title: 'Contextual Alerts', desc: 'Receive overspeed, braking, and impact alerts in the app.' },
    { icon: 'bx-gift', title: 'Varroc Miles', desc: 'Earn rewards for safe driving through the connected app experience.' },
    { icon: 'bx-store-alt', title: 'E-commerce Access', desc: 'Access offers and services in-app without leaving the ecosystem.' },
  ];
  troubleshooting = [
    { issue: 'Dashcam not turning on', fix: 'Check the power cable and LED status.' },
    { issue: 'Recording issue', fix: 'Use a Class 10 SD card with 32GB to 256GB capacity.' },
    { issue: 'Unclear video', fix: 'Clean the lens and windshield before driving.' },
    { issue: 'Device shuts down', fix: 'Do not hot-swap the SD card while the unit is active.' },
    { issue: 'Black screen on phone', fix: 'Clear the phone cache and reconnect through the app.' },
  ];
  ledIndicators = [
    { color: 'Blue', status: 'Slow blink', meaning: 'Recording' },
    { color: 'Red', status: 'Slow blink', meaning: 'Idle' },
  ];
  safetyTips = [
    'Do not remove the SD card while recording is in progress.',
    'Use the product as per local laws and driving regulations.',
    'Operating temperature range: -20°C to 70°C.',
    'Avoid high humidity and heavy dust exposure.',
    'Keep the app and device firmware updated for the best experience.',
  ];

  constructor(
    private notifyMeService: NotifyMeService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    this.notifyQueryParamSubscription = this.route.queryParamMap.pipe(
      map(params => params.get('notify-me') === 'true'),
      distinctUntilChanged()
    ).subscribe(shouldOpen => {
      if (shouldOpen) {
        this.clearNotifySuccessTimeout();
        this.isNotifySubmitSuccess = false;
        this.isNotifyModalOpen = true;
      } else {
        this.resetNotifyModal();
      }

      this.syncNotifyDialog();
    });
  }

  ngAfterViewInit() {
    this.syncNotifyDialog();
    try { AOS.refresh(); } catch (e) {}
  }

  ngOnDestroy() {
    this.clearNotifySuccessTimeout();
    this.notifyQueryParamSubscription?.unsubscribe();
  }

  scrollTo(id: string) {
    this.activeSection = id;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  openNotifyMeModal() {
    this.clearNotifySuccessTimeout();
    this.isNotifySubmitSuccess = false;
    this.isNotifyModalOpen = true;
    this.syncNotifyDialog();

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { 'notify-me': true },
      queryParamsHandling: 'merge'
    });
  }

  closeNotifyMeModal() {
    this.resetNotifyModal();
    this.syncNotifyDialog();

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { 'notify-me': null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  isNotifyFormValid(): boolean {
    return this.notifyForm.name.trim().length > 1
      && this.isEmailFormatValid(this.notifyForm.email.trim())
      && this.isPhoneFormatValid(this.notifyForm.phone.trim());
  }

  isNotifyEmailInvalid(): boolean {
    const email = this.notifyForm.email.trim();
    return email.length > 0 && !this.isEmailFormatValid(email);
  }

  isNotifyPhoneInvalid(): boolean {
    const phone = this.notifyForm.phone.trim();
    return phone.length > 0 && !this.isPhoneFormatValid(phone);
  }

  onNotifyPhoneInput() {
    this.notifyForm.phone = this.notifyForm.phone.replace(/\D/g, '').slice(0, 10);
  }

  submitNotifyMe() {
    if (!this.isNotifyFormValid() || this.isSubmittingNotify || this.isNotifySubmitSuccess) {
      return;
    }

    const nameParts = this.notifyForm.name.trim().split(/\s+/);
    const payload: NotifyMePayload = {
      productName: 'Camzo by Varroc Connect',
      customerName: nameParts[0] || '',
      customerLastName: nameParts.slice(1).join(' ') || '',
      customerEmail: this.notifyForm.email.trim(),
      customerCellNumber: this.notifyForm.phone.trim()
    };

    this.isSubmittingNotify = true;
    this.notifyMeService.submitNotifyMe(payload).subscribe({
      next: () => {
        this.isSubmittingNotify = false;
        this.isNotifySubmitSuccess = true;
        this.notifySuccessTimeoutId = setTimeout(() => this.closeNotifyMeModal(), 5000);
      },
      error: () => {
        this.isSubmittingNotify = false;
      }
    });
  }

  private resetNotifyModal() {
    this.clearNotifySuccessTimeout();
    this.isNotifyModalOpen = false;
    this.isSubmittingNotify = false;
    this.isNotifySubmitSuccess = false;
    this.notifyForm = { name: '', email: '', phone: '' };
  }

  private syncNotifyDialog() {
    const dialog = this.notifyDialog?.nativeElement;

    if (!dialog) {
      return;
    }

    if (this.isNotifyModalOpen && !dialog.open) {
      dialog.showModal();
    } else if (!this.isNotifyModalOpen && dialog.open) {
      dialog.close();
    }
  }

  private clearNotifySuccessTimeout() {
    if (this.notifySuccessTimeoutId) {
      clearTimeout(this.notifySuccessTimeoutId);
      this.notifySuccessTimeoutId = null;
    }
  }

  private isEmailFormatValid(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private isPhoneFormatValid(phone: string): boolean {
    return /^\d{10}$/.test(phone);
  }
}
